import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import moment from 'moment';
import { RequestsService } from 'src/app/services/requests/requests.service';
import { AlertBannerService, AlertType } from "../../lib/alert-banner";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { WarningModalContentComponent } from '../../lib/warning-modal/warning-modal.component';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import {ReportService} from "../../services/report/report.service";
import {Router} from "@angular/router";

@Component({
  selector: 'app-exits',
  templateUrl: './exits.component.pug',
  styleUrls: ['./exits.component.scss'],
  standalone: false
})
export class ExitsComponent implements OnInit, OnChanges {

  constructor(
    private modalService: BsModalService,
    private alertList: AlertBannerService,
    private requests: RequestsService,
    private router: Router,
    private report: ReportService,
    private permissions: PermissionsService
  ) { }

  @Input() searchOption: any = undefined;
  @Input() rowsPerView = 20;
  @Input() currentPage = 1;
  @Input() sortString = '-created';
  @Input() filterOutput: any;
  @Input() backgroundRefreshCounter: number = 0;

  @Output() viewReport: EventEmitter<any> = new EventEmitter();
  @Output() outputLoading = new EventEmitter();
  @Output() outputTotal = new EventEmitter();
  @Output() outputNoData = new EventEmitter();
  @Output() outputSortOptions = new EventEmitter();
  @Output() outputFilterStatusOptions = new EventEmitter();

  // FEATURE FLAG START
  canViewExitTab = true;
  // FEATURE FLAG END

  sortOptions = [
    {
      'label': $localize`Requested date`,
      'type': "number",
      'value': "created"
    },
    {
      'label': $localize`Employee`,
      'type': "word",
      'value': "name"
    }
  ];

  coloredTag = {
    'red': 'cherry',
    'green': 'forest',
    'yellow': 'amber',
    'grey': 'default-stat-tag'
  }

  filterStatusOptions = [
    {'label': $localize`Invited`, 'value': 'Invited'},
    {'label': $localize`In progress`, 'value': 'In progress'},
    {'label': $localize`Submitted`, 'value': 'Submitted'},
    {'label': $localize`Verified`, 'value': 'Verified'},
    {'label': $localize`Declined`, 'value': 'Declined'},
    { 'label': $localize`Expired`, 'value': 'Expired' }
  ];

  sendDateLabel = $localize`Send date`;

  requestsData: any;
  loading = true;
  total = 5;
  initialDataRequest = true;
  noData = false;
  deleteRequestWarningModal: BsModalRef = new BsModalRef();
  ENT_9003_FE_REQUESTS_PERSISTENT_DATA = false;

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.ENT_9003_FE_REQUESTS_PERSISTENT_DATA = this.permissions.isFeatureActivated('ent-9003-fe-requests-persistent-data');
    this.sortOptions[0] = {...this.sortOptions[0], label : $localize`Send date`}
    this.canViewExitTab = (this.permissions.hasPermission('can_view_exit_requests_list') || this.permissions.hasPermission('can_view_exit_requests_list_with_survey_perms') || this.permissions.hasPermission('can_view_own_exit_requests_list'));
    if (!this.canViewExitTab) {
      this.loading = false;
    }

    if (this.canViewExitTab) {
      this.initialiseData();
      this.emitToParent();
    }
    this.sortOptions.push({
      'label': $localize`View Status`,
      'type': "viewed",
      'value': "viewed"
    });

  }

  ngOnChanges(changes: SimpleChanges): void {
    // Background refresh - silent refresh without loading indicator
    if (this.ENT_9003_FE_REQUESTS_PERSISTENT_DATA && (changes.backgroundRefreshCounter && !changes.backgroundRefreshCounter.firstChange)) {
      this.initialiseData(true);
      return;
    }

    if (!this.initialDataRequest &&
      (changes.searchOption || changes.rowsPerView || changes.currentPage || changes.sortString || changes.filterOutput)) {
        this.initialiseData();
        this.emitToParent();
    }
  }

  initialiseData(silent: boolean = false) {
    this.initialDataRequest = this.areDefaultValues()

    // Couldn't find a more viable method to replace default sort option on app-exit initialise. Best I could find for the moment
    if(this.sortString.includes('candidate_name')) { this.sortString = this.sortString.replace('candidate_name', 'name'); }
    this.requests.getRequestsDuplicateParams(this.searchOption, this.rowsPerView, this.currentPage, this.sortString, this.filterOutput).subscribe(
    (res) => {
      this.requestsData = res.data.exits;
      this.total = res.data.total;
      // if first/initial data request returns no data, means the page has no data at all, so show
      // noData image, don't show in any other circumstance eg. searches etc.
      if (this.total < 1 && this.initialDataRequest) {
        this.noData = true;
      } else {
        this.convertData();
        this.initialDataRequest = false;
      }

      this.requestsData.forEach(request => {
        request.actionButtonData = this.populateActionButton(request);
      });

      this.loading = false;
      this.emitToParent();
    }, error => {
      console.log(error);
      this.initialDataRequest = false;
      if (error.status != 403) {
        this.alertList.append([{
          type: AlertType.DANGER,
          message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
          keepOnRouteChange: true
        }]);
      }
      this.loading = false;
      this.emitToParent();
    });

  }

  emitToParent() {
    this.outputLoading.emit(this.loading);
    this.outputTotal.emit(this.total);
    this.outputNoData.emit(this.noData);
    this.outputSortOptions.emit(this.sortOptions);
    this.outputFilterStatusOptions.emit(this.filterStatusOptions);
  }

  convertData() {
    this.requestsData.forEach(request => {
      if (request.requested_date) {
        let requested_date = moment(request.requested_date);
        request.requested_date_formatted = requested_date.format('ll');
      }
      if (request.employee) {
        let initials = request.employee.split(' ');
        let arrFiltered = initials.filter(el => {
          return el != null && el != '';
        });
        if (arrFiltered.length >= 2) {
          request.initials = arrFiltered[0][0] + arrFiltered[1][0];
        } else {
          request.initials = arrFiltered[0][0];
        }
      }
      this.setStatusColour(request);
      request.status_original = request.status;
      request.status = this.filterStatusOptions.find(obj => obj.value === request.status)?.label
    });
  }

  setStatusColour(request) {
    let status = request.status.toLowerCase();
    if (status === 'submitted' || status === 'verified') {
      request.status_colour = 'green';
    }
    else if (status === 'in progress') {
      request.status_colour = 'yellow';
    }
    else if (status === 'declined' || status === 'deleted' || status === 'expired') {
      request.status_colour = 'red';
    }
    else if (status === 'invited') {
      request.status_colour = 'grey';
    }
  }

  deleteRequest(request) {
    const initialState = {
      'title': $localize`Delete request?`,
      'message': $localize`Are you sure you want to delete the request for ${request.employee}?`
    }
    this.deleteRequestWarningModal = this.modalService.show(WarningModalContentComponent,
      { class: 'modal-dialog-centered default-modal', initialState }
    );

    this.deleteRequestWarningModal.content.closeBtnName = 'Close';
    this.deleteRequestWarningModal.content.selection.subscribe(deleteBtnClicked => {
      if (deleteBtnClicked) {
        this.requests.deleteRequest(request.uid).subscribe(
          (res) => {
            this.requests.clearCache(); // Clear cache on successful action
            this.requestsData = this.requestsData.filter(exit_request => { return exit_request.uid !== request.uid })
            this.alertList.append([{
              type: AlertType.SUCCESS,
              message: $localize`Success! The request has been deleted.`,
              keepOnRouteChange: true
            }])
          }, error => {
            console.log(error);
            this.alertList.append([{
              type: AlertType.DANGER,
              message: $localize`This request has been started by the recipient and can no longer be deleted.`,
              keepOnRouteChange: true
            }]);
          })
      }
    });
  }

  onViewReport(id) {
    this.viewReport.emit(id);
  }

  onClickRequestExitCard(event: any, request) {
    if(['Submitted', 'Verified'].includes(request.status_original))
    {
      this.viewReport.emit(request.uid);
    }
  }

  onClickName(event: any, request) {
    this.router.navigate([`/people/profile/${request.exitee_person_uuid}`]);

  }

  populateActionButton(request) {
    if (this.isDeleteRequestVisible(request))
    {
      return [{key: '1', value: $localize`Delete request`, disabled: request.is_restricted}];
    }
    return [{key: '0', value: $localize`No actions available`}];
  }

  handleKebabAction(event, request) {
    if(event.key == 1)
    {
      this.deleteRequest(request);
    }
  }

  isDeleteRequestVisible(request){
    return request.status == this.filterStatusOptions.find(obj => obj.value === "Invited")?.label;
  }
  isNoActionAvailableVisible(request){
    return request.status != this.filterStatusOptions.find(obj => obj.value === "Invited")?.label;
  }

  getRequestedByInfo(request){
    return {label: $localize`Requested by`, content: request.requested_by}
  }

  getSurveyInfo(request){
    return {label: $localize`Survey`, content: request.questionnaire}
  }

  getStatusInformation(request)
  {
    return {
      hasTag: true,
      nestedStatTagText: '',
      tagText: request.status,
      status: request.status_original,
      tagCompleted: 0,
      tagTotal: 0,
      pulseCutoffIncomplete: false,
      cancelledBy: ''
    }
  }

  areDefaultValues() {
    const isSearchOptionDefault = this.searchOption?.length === 0
    const isrowsPerViewDefault = this.rowsPerView === 20
    const iscurrentPageDefault = this.currentPage === 1
    const isSortStringDefault = this.sortString === '-created'
    const isfilterOutputDefault = this.filterOutput === null
    return isSearchOptionDefault && isrowsPerViewDefault && iscurrentPageDefault && isSortStringDefault && isSortStringDefault && isfilterOutputDefault
  }
}
